import { DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  ChannelFilters,
  ChannelTag,
  Country,
  GET_BUSINESS_AREA_TAGS,
  GET_CHANNEL_RELATED_CHALLENGE,
  GET_CHANNEL_RELATED_COUNTRIES,
  GET_CHANNEL_RELATED_REVIEWERS,
  GET_CHANNEL_RELATED_SPACES,
  GET_CHANNEL_TAGS,
  GET_REGIONS,
  GET_TOPICS,
  Innovation,
  IntrapreneurChallenge,
  PocBusinessArea,
  Region,
  Topic,
  User,
} from '@frontend/data-access';
import {
  selectChannelFilters,
  setChannelFilters,
} from '@frontend/feature-idea-channel';
import { Button, Cascader, Checkbox, Divider, Form, Input, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../channel-store/settings/hooks';
import styles from './idea-filters.module.less';

const CheckboxGroup = Checkbox.Group;

export const findPathsByValue = (
  options: { value: string; children?: any[] }[],
  values: string[]
): string[][] => {
  const result: string[][] = [];
  const traverse = (
    node: { value: string; children?: any[] },
    path: string[] = []
  ) => {
    const newPath = [...path, node.value];
    if (values?.includes(node.value)) {
      result.push(newPath);
    }
    if (node.children) {
      node.children.forEach((child) => traverse(child, newPath));
    }
  };
  options?.forEach((option) => traverse(option));
  return result;
};

/* eslint-disable-next-line */
export interface IdeaFiltersProps {
  isOpen: boolean;
  handleClose: () => void;
}

export function IdeaFilters({ isOpen, handleClose }: IdeaFiltersProps) {
  const router = useRouter();
  const channelId = router.query['id'];

  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectChannelFilters);
  const filtersLength = Object.values(formValues || {}).filter(
    (item) =>
      item === 'sort' ||
      ((typeof item === 'string' || Array.isArray(item)) && item.length)
  ).length;

  const { t } = useTranslation('common');

  const { data: challenges } = useQuery<{
    listRelatedChallenge: IntrapreneurChallenge[];
  }>(GET_CHANNEL_RELATED_CHALLENGE, {
    variables: {
      channelId: channelId,
    },
  });
  const { data: spaces } = useQuery<{
    listRelatedInnovationSpace: Innovation[];
  }>(GET_CHANNEL_RELATED_SPACES, {
    variables: {
      channelId: channelId,
    },
  });
  const { data: countries } = useQuery<{
    listRelatedCountries: Country[];
  }>(GET_CHANNEL_RELATED_COUNTRIES, {
    variables: {
      channelId: channelId,
    },
  });
  const { data: businessArea } = useQuery<{
    pocBusinessAreaTags: PocBusinessArea[];
  }>(GET_BUSINESS_AREA_TAGS);

  const filteredBusinessArea =
    businessArea?.pocBusinessAreaTags.filter((i) => i.parentName === '') || [];

  const businessAreaOptions = filteredBusinessArea.map((b) => ({
    label: b.name,
    value: b.name,
    key: b.id,
    children: businessArea?.pocBusinessAreaTags
      .filter((c) => c.parentName === b.name)
      .map((d) => ({
        label: d.name,
        value: d.name,
        key: b.id,
      })),
  }));

  const { data: reviewers } = useQuery<{
    listRelatedReviewers: User[];
  }>(GET_CHANNEL_RELATED_REVIEWERS, {
    variables: {
      channelId: channelId,
    },
  });
  const { data: dataTopics } = useQuery<{
    topics: Topic[];
  }>(GET_TOPICS);

  const { data: dataRegions } = useQuery<{
    regions: Region[];
  }>(GET_REGIONS);
  const { data: dataChannelTags } = useQuery<{
    channelTags: ChannelTag[];
  }>(GET_CHANNEL_TAGS);

  const innovationSpaceTags =
    spaces?.listRelatedInnovationSpace.map((item) => ({
      value: item.id as string,
      label: item.name,
    })) || [];

  const [inputChallengeName, setInputChallengeName] = useState<string>();

  const intraChallengeOptions = useMemo(() => {
    return challenges?.listRelatedChallenge
      ? inputChallengeName
        ? challenges?.listRelatedChallenge.filter((item) =>
            item.title.toLowerCase().includes(inputChallengeName.toLowerCase())
          )
        : challenges?.listRelatedChallenge
      : [];
  }, [challenges, inputChallengeName]);

  const agingItems = [
    {
      label: t('idea_filters_aging_0_5'),
      value: '{"min":0,"max":5}',
    },
    {
      label: t('idea_filters_aging_6_15'),
      value: '{"min":6,"max":15}',
    },
    {
      label: t('idea_filters_aging_16_30'),
      value: '{"min":16,"max":30}',
    },
    {
      label: t('idea_filters_aging_31_60'),
      value: '{"min":31,"max":60}',
    },
    {
      label: t('idea_filters_aging_61_90'),
      value: '{"min":61,"max":90}',
    },
    {
      label: t('idea_filters_aging_90_plus'),
      value: '{"min":91,"max":null}',
    },
  ];
  const scoreOptions = [
    {
      key: 'nan',
      label: (
        <span className="px-2 py-1 bg-gray-300 rounded-[50px] text-xs">
          {t('idea_filters_score_na')}
        </span>
      ),
      value: '{"min":null,"max":null}',
    },
    {
      key: '1-3',
      label: (
        <span className="px-2 py-1 bg-red-100 text-red-600 rounded-[50px] text-xs">
          {t('idea_filters_score_1_3')}
        </span>
      ),
      value: '{"min":1,"max":3}',
    },
    {
      key: '4-6',
      label: (
        <span className="px-2 py-1 bg-orange-100 text-orange-600 rounded-[50px] text-xs">
          {t('idea_filters_score_4_6')}
        </span>
      ),
      value: '{"min":4,"max":6}',
    },
    {
      key: '7-9',
      label: (
        <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-[50px] text-xs">
          {t('idea_filters_score_7_9')}
        </span>
      ),
      value: '{"min":7,"max":9}',
    },
    {
      key: '10',
      label: (
        <span className="px-2 py-1 bg-green-100 text-green-600 rounded-[50px] text-xs">
          {t('idea_filters_score_10')}
        </span>
      ),
      value: '{"min":10,"max":10}',
    },
  ];
  const metricsOptions = [
    {
      key: 'none',
      label: (
        <span className="px-2 py-1 bg-gray-300 rounded-[50px] text-xs">
          {t('idea_filters_metrics_none')}
        </span>
      ),
      value: 'NONE',
    },
    {
      key: 'no_target',
      label: (
        <span className="px-2 py-1 bg-red-100 text-red-600 rounded-[50px] text-xs">
          {t('idea_filters_metrics_no_target')}
        </span>
      ),
      value: 'NO_TARGET',
    },
    {
      key: 'target',
      label: (
        <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-[50px] text-xs">
          {t('idea_filters_metrics_target')}
        </span>
      ),
      value: 'TARGET',
    },
    {
      key: 'result',
      label: (
        <span className="px-2 py-1 bg-green-100 text-green-600 rounded-[50px] text-xs">
          {t('idea_filters_metrics_result')}
        </span>
      ),
      value: 'RESULT',
    },
  ];

  const handleClickReset = () => {
    form.resetFields();
  };

  const handleFinish = (values: ChannelFilters) => {
    dispatch(
      setChannelFilters({
        ...filters,
        ...values,
        businessAreas: values.businessAreas?.map((v) => v[v.length - 1]),
        scores: values.scores?.map((item) => JSON.parse(item)),
        days: values.days?.map((item) => JSON.parse(item)),
      })
    );
    handleClose();
  };

  const [showMore, setShowMore] = useState<string>();

  const handleShowMore = (value: string) => {
    setShowMore((prev) => (prev === value ? undefined : value));
  };

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        ...filters,
        businessAreas: filters?.businessAreas
          ? findPathsByValue(businessAreaOptions, filters.businessAreas)
          : undefined,
        scores: filters?.scores
          ? filters.scores.map((item) => JSON.stringify(item))
          : undefined,
        days: filters?.days
          ? filters.days.map((item) => JSON.stringify(item))
          : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters, form]);

  return (
    <div className={styles['container']}>
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <div className="overflow-y-auto max-h-[80vh]">
          <Form.Item name="scores" label={t('idea_filters_scores')}>
            <CheckboxGroup className="!flex !gap-1 !items-center">
              {scoreOptions.map((item) => (
                <div key={item.key}>
                  <Checkbox value={item.value}>{item.label}</Checkbox>
                </div>
              ))}
            </CheckboxGroup>
          </Form.Item>
          <Divider />
          <Form.Item name="days" label={t('idea_filters_idea_days')}>
            <CheckboxGroup className="!grid !grid-cols-3">
              {agingItems.map((item) => (
                <div key={item.label}>
                  <Checkbox value={item.value}>{item.label}</Checkbox>
                </div>
              ))}
            </CheckboxGroup>
          </Form.Item>
          <Divider />
          <Form.Item name="metricTags" label={t('idea_filters_metrics')}>
            <CheckboxGroup className="!flex !gap-1 !items-center">
              {metricsOptions.map((item) => (
                <div key={item.key}>
                  <Checkbox value={item.value}>{item.label}</Checkbox>
                </div>
              ))}
            </CheckboxGroup>
          </Form.Item>
          <Divider />
          <Form.Item label={t('idea_filters_challenge_name')}>
            <Input
              placeholder={t('idea_filters_search_by_name')}
              suffix={<SearchOutlined />}
              className="mb-3"
              onChange={(e) => setInputChallengeName(e.target.value.trim())}
            />
            <Form.Item name="challengeIds" noStyle>
              <CheckboxGroup className="!flex !flex-col">
                {(showMore === 'challengeIds'
                  ? intraChallengeOptions
                  : intraChallengeOptions.slice(0, 4)
                ).map((item) => (
                  <div key={item.id}>
                    <Checkbox value={item.id}>{item.title}</Checkbox>
                  </div>
                ))}
              </CheckboxGroup>
            </Form.Item>
            {intraChallengeOptions.length > 4 && (
              <div
                className="cursor-pointer text-primary-600"
                onClick={() => handleShowMore('challengeIds')}
              >
                {showMore === 'challengeIds' ? (
                  <>
                    <UpOutlined /> {t('idea_filters_show_less')}
                  </>
                ) : (
                  <>
                    <DownOutlined /> {t('idea_filters_show_more')}
                  </>
                )}
              </div>
            )}
          </Form.Item>
          <Divider />
          <Form.Item label={t('idea_filters_innovation_space')}>
            <Form.Item name="innovationSpaceIds" noStyle>
              <CheckboxGroup
                className="!grid !grid-cols-2"
                options={
                  showMore === 'innovationSpaceIds'
                    ? innovationSpaceTags
                    : innovationSpaceTags.slice(0, 4)
                }
              />
            </Form.Item>
            {innovationSpaceTags?.length > 4 && (
              <div
                className="cursor-pointer text-primary-600"
                onClick={() => handleShowMore('innovationSpaceIds')}
              >
                {showMore === 'innovationSpaceIds' ? (
                  <>
                    <UpOutlined /> {t('idea_filters_show_less')}
                  </>
                ) : (
                  <>
                    <DownOutlined /> {t('idea_filters_show_more')}
                  </>
                )}
              </div>
            )}
          </Form.Item>
          <Divider />
          <Form.Item
            name="businessAreas"
            label={t('business_functional_area')}
            className="!mb-0"
          >
            <Cascader
              multiple
              options={businessAreaOptions}
              placeholder={t('select_business_area')}
            />
          </Form.Item>
          <Divider />
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="topicIds" label={t('Topics')} className="!mb-0">
              <Select
                allowClear
                placeholder={t('idea_filters_select_topics')}
                mode="multiple"
                maxTagCount="responsive"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={dataTopics?.topics.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>
            <Form.Item name="tagIds" label={t('idea_tags')} className="!mb-0">
              <Select
                allowClear
                placeholder={t('idea_filters_select_idea_tags')}
                mode="multiple"
                maxTagCount="responsive"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={dataChannelTags?.channelTags.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>
          </div>
          <Divider />
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="regionIds" label={t('Regions')} className="!mb-0">
              <Select
                allowClear
                placeholder={t('idea_filters_select_regions')}
                mode="multiple"
                maxTagCount="responsive"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={dataRegions?.regions.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="countryIds"
              label={t('idea_filters_country_s')}
              className="!mb-0"
            >
              <Select
                allowClear
                placeholder={t('idea_filters_select_countries')}
                mode="multiple"
                maxTagCount="responsive"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={countries?.listRelatedCountries.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
              />
            </Form.Item>
          </div>
          <Divider />
          <Form.Item name="reviewerIds" label={t('reviewers')}>
            <CheckboxGroup className="!grid !grid-cols-2">
              {reviewers?.listRelatedReviewers.map((item) => (
                <div key={item.id}>
                  <Checkbox value={item.id}>{item.fullName}</Checkbox>
                </div>
              ))}
            </CheckboxGroup>
          </Form.Item>
        </div>
        <div className="flex justify-between items-center gap-2 mt-4">
          <Button type="link" onClick={handleClickReset}>
            {t('idea_filters_clear_all')}{' '}
            {filtersLength > 0 && `(${filtersLength})`}
          </Button>
          <Button type="primary" shape="round" htmlType="submit">
            {t('confirm')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default IdeaFilters;
